import { Fragment } from 'react';

import LookupIcon from '@usas/ui-web/components/icons/LookupIcon';
import EditIcon from '@usas/ui-web/components/icons/EditIcon';
import DeleteIcon from '@usas/ui-web/components/icons/DeleteIcon';

import { formatDateTimeTwelveHourClock } from '@usas/ui-web/utils/DateFunctions';
import { WORKFLOW_STATUS_COMPLETE } from '@usas/ui-web/utils/Constants';

import global from '@usas/ui-web/components/GlobalStyle.module.css';

const LargeGrid = ({ data, onEdit, onDelete }) => {
  return (
    <table className={[global.UsasTable, 'visible-lg visible-md'].join(' ')}>
      <thead>
        <tr>
          <th>OME Meet Name</th>
          <th style={{ minWidth: '305px' }}>Entry Start - End Dates</th>
          <th style={{ minWidth: '125px' }}>Status</th>
          <th style={{ minWidth: '90px' }}></th>
        </tr>
      </thead>
      <tbody>
        {data.map((omeMeetEntry, i) => {
          return (
            <Fragment key={i}>
              <tr key={i}>
                <td>{omeMeetEntry.omeMeetName || ''}</td>
                <td>{omeMeetEntry?.meetEntryStartDate ? formatDateTimeTwelveHourClock(omeMeetEntry?.meetEntryStartDate) : ''} {omeMeetEntry?.meetEntryStartDate ? "-" : ''} {omeMeetEntry?.meetEntryEndDate ? formatDateTimeTwelveHourClock(omeMeetEntry?.meetEntryEndDate) : ''}</td>
                <td>{omeMeetEntry.status || ''}</td>
                <td>
                  {onEdit && (omeMeetEntry.isEditable === true || omeMeetEntry.status !== WORKFLOW_STATUS_COMPLETE) ?
                    <button className={global.IconButtonMargin}
                      type="button"
                      onClick={(e) => onEdit(e, omeMeetEntry)}>
                      <EditIcon />
                    </button> :
                    <button className={global.IconButtonMargin}
                      type="button"
                      onClick={(e) => onEdit(e, omeMeetEntry)}>
                      <LookupIcon toolTipText='View' />
                    </button>}
                  {onDelete && omeMeetEntry?.isMainEntry === false && omeMeetEntry.canBeDeleted === true &&
                    <button className={global.IconButtonMargin}
                      type="button"
                      onClick={(e) => onDelete(e, omeMeetEntry)}>
                      <DeleteIcon />
                    </button>}
                </td>
              </tr>
            </Fragment>
          )
        })}
      </tbody>
    </table>
  );
}

const SmallGrid = ({ data, onEdit, onDelete }) => (
  <div className={[global.SmallTable, 'hidden-lg hidden-md'].join(' ')}>
    {data.map((omeMeetEntry, i) => {
      return (
        <div className={global.SmallTableRow} key={i}>
          <div className={global.SmallTableRowHead}>
            <span className='hidden-xs'>{omeMeetEntry.omeMeetName || <span>&nbsp;</span>}&nbsp;</span>&nbsp;
            {onDelete && omeMeetEntry?.isMainEntry === false && omeMeetEntry.canBeDeleted === true &&
              <button className={global.IconButton}
                type="button"
                onClick={(e) => onDelete(e, omeMeetEntry)}>
                <DeleteIcon />
              </button>}
            {onEdit && (omeMeetEntry.isEditable === true || omeMeetEntry.status !== WORKFLOW_STATUS_COMPLETE) ?
              <button className={onDelete && omeMeetEntry?.isMainEntry === false ? global.IconButtonMobileMargin : global.IconButton}
                type="button"
                onClick={(e) => onEdit(e, omeMeetEntry)}>
                <EditIcon />
              </button> :
              <button className={onDelete && omeMeetEntry?.isMainEntry === false ? global.IconButtonMobileMargin : global.IconButton}
                type="button"
                onClick={(e) => onEdit(e, omeMeetEntry)}>
                <LookupIcon toolTipText='View' />
              </button>}
          </div>
          <div className={global.SmallTableRowInfo} key={i}>
            <div className='row'>
              <div className={[global.SmallTableRowLabels, 'col-xs-6 visible-xs'].join(' ')}>OME Meet Name</div>
              <div className={[global.SmallTableRowData, 'col-xs-6 visible-xs'].join(' ')}>{omeMeetEntry.omeMeetName || <span>&nbsp;</span>}</div>
              <div className={[global.SmallTableRowLabels, 'col-xs-6 col-sm-3'].join(' ')}>Entry Start Date</div>
              <div className={[global.SmallTableRowData, 'col-xs-6 col-sm-3'].join(' ')}>{omeMeetEntry?.meetEntryStartDate === null ? <span>&nbsp;</span> : formatDateTimeTwelveHourClock(omeMeetEntry?.meetEntryStartDate)}</div>
              <div className={[global.SmallTableRowLabels, 'col-xs-6 col-sm-3'].join(' ')}>Entry End Date</div>
              <div className={[global.SmallTableRowData, 'col-xs-6 col-sm-3'].join(' ')}>{omeMeetEntry?.meetEntryEndDate === null ? <span>&nbsp;</span> : formatDateTimeTwelveHourClock(omeMeetEntry?.meetEntryEndDate)}</div>
              <div className={[global.SmallTableRowLabels, 'col-xs-6 col-sm-3'].join(' ')}>Time Zone</div>
              <div className={[global.SmallTableRowData, 'col-xs-6 col-sm-3'].join(' ')}>{omeMeetEntry?.timeZone || <span>&nbsp;</span>}</div>
              <div className={[global.SmallTableRowLabels, 'col-xs-6 col-sm-3'].join(' ')}>Status</div>
              <div className={[global.SmallTableRowData, 'col-xs-6 col-sm-3'].join(' ')}>{omeMeetEntry.status || <span>&nbsp;</span>}</div>
            </div>
          </div>
        </div>)
    })}
  </div>
);

const OmeMeetsGrid = ({ state, onEdit, onDelete }) => {
  if (Array.isArray(state.objData?.omeMeetEntry) === false || state.objData?.omeMeetEntry?.length === 0) {
    return (
      <Fragment>
        <table className={[global.UsasTable, 'visible-lg visible-md'].join(' ')}>
          <thead>
            <tr>
              <th>OME Meet Name</th>
              <th style={{ minWidth: '305px' }}>Entry Start - End Dates</th>
              <th style={{ minWidth: '180px' }}>Time Zone</th>
              <th style={{ minWidth: '125px' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colSpan="4">No OME Meets</td>
            </tr>
          </tbody>
        </table>
        <div className={[global.SmallTable, 'hidden-lg hidden-md'].join(' ')}>
          <div className={global.SmallTableRow}>
            <div className={global.SmallTableRowHead}>
              &nbsp;
            </div>
            <div className={global.SmallTableRowInfo}>
              <div className={global.SmallTableRowLabels}>No OME Meets</div>
            </div>
          </div>
        </div>
      </Fragment>
    );
  }
  return (
    <Fragment>
      <LargeGrid data={state.objData?.omeMeetEntry} onEdit={onEdit} onDelete={onDelete} />
      <SmallGrid data={state.objData?.omeMeetEntry} onEdit={onEdit} onDelete={onDelete} />
    </Fragment>
  );
}
export default OmeMeetsGrid;